import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, LineElement, PointElement,
  Title, Tooltip, Legend, Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { api } from '../api';
import { formatPts, formatDate, formatBattleName, formatDateTime, formatDuration, isLive, exportCSV, CLAN_COLORS } from '../utils';
import CountdownTimer from '../components/CountdownTimer';
import ClanRow from '../components/ClanPanel';

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend, Filler);

const CHART_OPTS = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: { labels: { color: '#7d8590', font: { size: 11 } } },
    tooltip: { callbacks: { label: ctx => ` ${ctx.dataset.label}: ${formatPts(ctx.raw)}` } },
  },
  scales: {
    x: { ticks: { color: '#7d8590', maxRotation: 45, font: { size: 10 } }, grid: { color: '#30363d' } },
    y: { ticks: { color: '#7d8590', callback: v => formatPts(v) }, grid: { color: '#30363d' } },
  },
};

export default function BattleDetail({ refreshKey }) {
  const { id } = useParams();
  const navigate = useNavigate();

  const [battle, setBattle]       = useState(null);
  const [standings, setStandings] = useState([]);
  const [bStats, setBStats]       = useState(null);
  const [timeline, setTimeline]   = useState(null);
  const [tab, setTab]   = useState('standings');
  const [filter, setFilter] = useState('');
  const [error, setError]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [disconnects, setDisconnects] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setTimeline(null);
    Promise.all([
      api.standings(id),
      api.battleStats(id).catch(() => null),
    ]).then(([{ battle: b, standings: s }, stats]) => {
      setBattle(b);
      setStandings(s);
      setBStats(stats);
      setLoading(false);
    }).catch(err => { setError(err.message); setLoading(false); });
  }, [id, refreshKey]);

  useEffect(() => {
    if (tab === 'timeline' && !timeline) {
      api.timeline(id).then(d => setTimeline(d)).catch(() => setTimeline({}));
    }
  }, [tab, id, timeline]);

  useEffect(() => {
    if (tab === 'disconnects' && !disconnects) {
      api.disconnects(200, id).then(setDisconnects).catch(() => setDisconnects([]));
    }
  }, [tab, id, disconnects]);

  if (loading) return <div className="page-center"><div className="spinner" /></div>;
  if (error)   return <div className="page"><div className="error-box">{error}</div></div>;
  if (!battle) return <div className="page"><div className="empty-msg">Battle not found.</div></div>;

  const live      = isLive(battle);
  const name      = formatBattleName(battle.clanBattleName);
  const totalPts  = standings.reduce((s, r) => s + Number(r.totalPoints), 0);
  const totalPl   = standings.reduce((s, r) => s + Number(r.playerCount), 0);
  const maxPts    = Number(standings[0]?.totalPoints || 1);
  const elapsed   = bStats?.elapsedHours || 0;
  const ptsPerHr  = elapsed > 0 ? totalPts / elapsed : 0;
  const gap       = standings.length >= 2 ? Number(standings[0].totalPoints) - Number(standings[1].totalPoints) : null;

  const q = filter.toLowerCase();
  const filtered = standings.filter(r => !q || r.name.toLowerCase().includes(q));

  // Timeline chart data
  let timelineChart = null;
  if (tab === 'timeline' && timeline?.timeline) {
    const tl = timeline.timeline;
    const clans = Object.keys(tl);
    const allHours = [...new Set(clans.flatMap(c => tl[c].map(p => p.hour)))].sort();
    timelineChart = {
      labels: allHours.map(h => h.replace('T', ' ').slice(0, 16)),
      datasets: clans.map((clan, i) => {
        const map = Object.fromEntries(tl[clan].map(p => [p.hour, p.points]));
        return {
          label: clan,
          data: allHours.map(h => map[h] ?? null),
          borderColor: CLAN_COLORS[i % CLAN_COLORS.length],
          backgroundColor: `${CLAN_COLORS[i % CLAN_COLORS.length]}20`,
          borderWidth: 2,
          pointRadius: 2,
          spanGaps: true,
          fill: false,
          tension: 0.3,
        };
      }),
    };
  }

  function doExport() {
    exportCSV(
      [['Rank', 'Clan', 'Total Points', 'Players'], ...standings.map((s, i) => [i + 1, s.name, s.totalPoints, s.playerCount])],
      `${battle.clanBattleName}-standings.csv`
    );
  }

  return (
    <div className="page fade-in">
      {/* Back */}
      <button className="btn" style={{ marginBottom: 16 }} onClick={() => navigate('/battles')}>
        ← Back
      </button>

      {/* Hero */}
      <div className="hero">
        <div className="hero-top">
          <div>
            <div className="hero-title">{name}</div>
            <div className="hero-meta">
              {live
                ? <span className="badge badge-live">Live</span>
                : <span className="badge badge-done">Finished</span>}
              <span>{formatDate(battle.createdAt)}</span>
            </div>
          </div>
          <div className="hero-right">
            {live && (
              <div className="stat-card">
                <CountdownTimer finishesAt={battle.finishesAt} />
                <div className="stat-lbl">Time Left</div>
              </div>
            )}
            <div className="stat-card">
              <div className="stat-val blue">{standings.length}</div>
              <div className="stat-lbl">Clans</div>
            </div>
            <div className="stat-card">
              <div className="stat-val">{totalPl}</div>
              <div className="stat-lbl">Players</div>
            </div>
            <div className="stat-card">
              <div className="stat-val amber">{formatPts(totalPts)}</div>
              <div className="stat-lbl">Total Pts</div>
            </div>
          </div>
        </div>
      </div>

      {/* Secondary stats */}
      {bStats && (
        <div className="stats-row mb-20">
          {elapsed > 0 && (
            <div className="stat-card">
              <div className="stat-val amber">{formatPts(Math.round(ptsPerHr))}</div>
              <div className="stat-lbl">Pts / Hour</div>
            </div>
          )}
          <div className="stat-card">
            <div className="stat-val">{bStats.totalRounds.toLocaleString()}</div>
            <div className="stat-lbl">Update Rounds</div>
          </div>
          {elapsed > 0 && (
            <div className="stat-card">
              <div className="stat-val">{elapsed}h</div>
              <div className="stat-lbl">Elapsed</div>
            </div>
          )}
          {gap !== null && (
            <div className="stat-card">
              <div className="stat-val purple">{formatPts(gap)}</div>
              <div className="stat-lbl">Leader Gap</div>
            </div>
          )}
          {totalPl > 0 && (
            <div className="stat-card">
              <div className="stat-val green">{formatPts(Math.round(totalPts / totalPl))}</div>
              <div className="stat-lbl">Avg / Player</div>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="tabs">
        {[['standings', 'Standings'], ['timeline', 'Timeline'], ['stats', 'Stats'], ['disconnects', 'Disconnects']].map(([key, label]) => (
          <button key={key} className={`tab-btn${tab === key ? ' active' : ''}`} onClick={() => setTab(key)}>
            {label}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <button className="btn" style={{ marginBottom: 2 }} onClick={doExport}>⬇ CSV</button>
      </div>

      {/* Standings tab */}
      {tab === 'standings' && (
        <>
          <div className="filter-bar">
            <input
              className="filter-input"
              placeholder="Filter clans…"
              value={filter}
              onChange={e => setFilter(e.target.value)}
            />
          </div>
          {filtered.length === 0 && <div className="empty-msg">No clans match.</div>}
          <div className="standings-wrap">
            {filtered.map((row, i) => (
              <ClanRow
                key={row.id}
                row={row}
                rank={standings.indexOf(row) + 1}
                battle={battle}
                maxPts={maxPts}
              />
            ))}
          </div>
        </>
      )}

      {/* Timeline tab */}
      {tab === 'timeline' && (
        <div className="chart-card">
          <div className="chart-title">Points per Clan Over Time</div>
          {!timeline && <div className="page-center"><div className="spinner" /></div>}
          {timeline && !timelineChart && <div className="empty-msg">No timeline data.</div>}
          {timelineChart && (
            <div className="chart-wrap" style={{ height: 340 }}>
              <Line data={timelineChart} options={CHART_OPTS} />
            </div>
          )}
        </div>
      )}

      {/* Stats tab */}
      {tab === 'stats' && bStats && (
        <div className="card">
          <div className="section-title" style={{ marginBottom: 16 }}>Battle Statistics</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
            {[
              ['Total Clans',    bStats.totalClans],
              ['Unique Players', bStats.uniquePlayers],
              ['Update Rounds',  bStats.totalRounds],
              ['Elapsed (h)',    bStats.elapsedHours],
              ['Total Points',   formatPts(bStats.grandTotal)],
              ['First Scan',     bStats.firstUpdate ? new Date(bStats.firstUpdate).toLocaleString() : '—'],
              ['Last Scan',      bStats.lastUpdate  ? new Date(bStats.lastUpdate).toLocaleString()  : '—'],
            ].map(([lbl, val]) => (
              <div key={lbl} className="stat-card">
                <div className="stat-val">{val}</div>
                <div className="stat-lbl">{lbl}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Disconnects tab */}
      {tab === 'disconnects' && (
        <>
          {!disconnects && <div className="page-center"><div className="spinner" /></div>}
          {disconnects && disconnects.length === 0 && (
            <div className="empty-msg">No disconnect events recorded for this battle.</div>
          )}
          {disconnects && disconnects.length > 0 && (
            <>
              <div className="stats-row mb-20">
                <div className="stat-card">
                  <div className="stat-val">{disconnects.length}</div>
                  <div className="stat-lbl">Total Events</div>
                </div>
                <div className="stat-card">
                  <div className="stat-val text-red">{disconnects.filter(d => !d.endTime).length}</div>
                  <div className="stat-lbl">Still Down</div>
                </div>
                <div className="stat-card">
                  <div className="stat-val text-green">{disconnects.filter(d => d.endTime).length}</div>
                  <div className="stat-lbl">Resolved</div>
                </div>
              </div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Player</th>
                      <th>Clan</th>
                      <th>Started</th>
                      <th>Duration</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {disconnects.map((d, i) => (
                      <tr key={i}>
                        <td>
                          <button
                            style={{ background: 'none', border: 'none', color: 'var(--blue)', cursor: 'pointer', fontWeight: 600, fontSize: '.88rem', padding: 0 }}
                            onClick={() => navigate(`/player/${d.robloxId}`)}
                          >
                            {d.username}
                          </button>
                        </td>
                        <td style={{ color: 'var(--muted)', fontSize: '.85rem' }}>{d.clanName || '—'}</td>
                        <td style={{ color: 'var(--muted)', fontSize: '.83rem' }}>{formatDateTime(d.startTime)}</td>
                        <td style={{ fontSize: '.85rem' }}>{d.durationMinutes != null ? formatDuration(d.durationMinutes) : '—'}</td>
                        <td>
                          {d.endTime
                            ? <span className="badge badge-done">Resolved</span>
                            : <span className="badge badge-live">Active</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
